import React from "react";
import { useAuth } from "@/lib/auth";
import { Search, Bell, Sparkles, Command, LogOut, User, Building2, Menu } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { roleLabel } from "@/lib/format";

export default function Topbar({ onOpenCmd, onToggleAi, onToggleMobileNav }) {
  const { user, tenant, logout } = useAuth();

  return (
    <header
      className="h-14 shrink-0 border-b border-zinc-900 bg-[#080808]/90 backdrop-blur-md flex items-center px-3 sm:px-5 gap-2 sm:gap-4"
      data-testid="topbar"
    >
      {/* Mobil hamburger */}
      <button
        onClick={onToggleMobileNav}
        className="lg:hidden p-2 -ml-1 rounded-md hover:bg-zinc-900 text-zinc-300 shrink-0"
        data-testid="mobile-nav-toggle"
        aria-label="Åpne meny"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Tenant selector — kompakt på mobil */}
      <button
        className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-2.5 py-1.5 rounded-md hover:bg-zinc-900 transition-colors text-sm min-w-0"
        data-testid="tenant-selector"
      >
        <div className="h-6 w-6 rounded bg-zinc-800 grid place-items-center shrink-0">
          <Building2 className="h-3.5 w-3.5 text-emerald-400" />
        </div>
        <span className="font-medium text-zinc-100 truncate max-w-[120px] sm:max-w-none">{tenant?.name || "—"}</span>
        <span className="hidden md:inline text-xs text-zinc-500">· {tenant?.orgnr}</span>
      </button>

      {/* Søk — full på desktop, ikon på mobil */}
      <button
        onClick={onOpenCmd}
        className="hidden md:flex flex-1 max-w-md items-center gap-2 px-3 py-1.5 rounded-md bg-zinc-900/60 border border-zinc-900 hover:border-zinc-800 transition-colors text-sm text-zinc-400"
        data-testid="topbar-search"
      >
        <Search className="h-3.5 w-3.5" />
        <span className="truncate">Søk i kunder, målepunkter, fakturaer…</span>
        <span className="ml-auto flex items-center gap-1 text-[10px] text-zinc-500 border border-zinc-800 rounded px-1.5 py-0.5">
          <Command className="h-3 w-3" /> K
        </span>
      </button>

      {/* Mobil søke-ikon */}
      <button
        onClick={onOpenCmd}
        className="md:hidden ml-auto p-2 rounded-md hover:bg-zinc-900 text-zinc-400"
        data-testid="topbar-search-mobile"
        aria-label="Søk"
      >
        <Search className="h-4 w-4" />
      </button>

      <div className="flex items-center gap-1 md:ml-0">
        {/* AI-knapp — kompakt på mobil */}
        <button
          onClick={onToggleAi}
          className="flex items-center gap-2 px-2.5 sm:px-3 py-1.5 rounded-md text-sm border border-zinc-900 hover:border-emerald-500/40 hover:bg-emerald-500/5 text-zinc-300 hover:text-emerald-400 transition-colors"
          data-testid="topbar-ai"
          aria-label="AI-assistent"
        >
          <Sparkles className="h-4 w-4" />
          <span className="hidden sm:inline">AI-assistent</span>
        </button>
        <button
          className="hidden sm:block p-2 rounded-md hover:bg-zinc-900 text-zinc-400"
          data-testid="topbar-notifications"
          aria-label="Varsler"
        >
          <Bell className="h-4 w-4" />
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 px-1.5 sm:px-2 py-1 rounded-md hover:bg-zinc-900" data-testid="user-menu">
              <div className="h-7 w-7 rounded-full bg-gradient-to-br from-emerald-500/30 to-blue-500/30 border border-zinc-800 grid place-items-center text-xs font-medium text-emerald-300 shrink-0">
                {user?.full_name?.[0] || "U"}
              </div>
              <div className="text-left leading-tight hidden lg:block">
                <div className="text-xs text-zinc-100 font-medium">{user?.full_name}</div>
                <div className="text-[10px] text-zinc-500">{roleLabel(user?.role)}</div>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-[#0c0c0c] border-zinc-800 w-56">
            <DropdownMenuLabel className="text-zinc-500">{user?.email}</DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-zinc-800" />
            <DropdownMenuItem className="text-zinc-300 focus:bg-zinc-900 focus:text-zinc-100" data-testid="menu-profile">
              <User className="h-4 w-4 mr-2" /> Min profil
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-zinc-800" />
            <DropdownMenuItem
              className="text-red-400 focus:bg-red-500/10 focus:text-red-300"
              onClick={logout}
              data-testid="menu-logout"
            >
              <LogOut className="h-4 w-4 mr-2" /> Logg ut
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
